import React from 'react';
import PropTypes from 'prop-types';
import Input from '@Components/Input';
import SimpleSpinner from '@Components/Results/utils/SimpleSpinner';
import { PASSWORD_ERRORS, validateConfirmationPassword, validatePassword } from '@Utils/Password';

const spinnerId = 'resetPasswordFormToken';

const errorsMessages = {
  [PASSWORD_ERRORS.REQUIRED]: 'Please choose a new password',
  [PASSWORD_ERRORS.LOWER_CASE]: 'Password must have at least one lower case character',
  [PASSWORD_ERRORS.UPPER_CASE]: 'Password must have at least one upper case character',
  [PASSWORD_ERRORS.DIGIT_OR_SPECIAL]: 'Password must have at least one numeric character or special characters',
  [PASSWORD_ERRORS.MIN_LENGTH]: 'Password must have minimum of 8 characters',
  [PASSWORD_ERRORS.MAX_LENGTH]: 'Password must have maximum of 200 characters',
  [PASSWORD_ERRORS.RESTRICT]: 'Password must not contain prohibited expression',
  [PASSWORD_ERRORS.SEQUENCE]: 'Password must not contain sequential alpha numeric characters',
  [PASSWORD_ERRORS.CHECK_ASCII]: 'Password must contain only printable, non-whitespace ASCII characters',
  [PASSWORD_ERRORS.COMPARE_TO]: 'The two passwords given are not the same. Please check your spelling',
  [PASSWORD_ERRORS.CONFIRM_PASSWORD_REQUIRED]: 'Please confirm new password',
};

function ResetPasswordWithTokenDialogContent({
  trackAdobeAnalyticsError,
  isLoading,
  newPassword,
  setNewPassword,
  confirmationPassword,
  setConfirmationPassword,
  requestSent,
}) {
  const handleNewPasswordChange = (value) => {
    const { isValid, errors } = validatePassword(value);
    setNewPassword({
      value, isValid, errors, dirty: true,
    });
    const confirmationPasswordValidation = validateConfirmationPassword(value, confirmationPassword.value);
    setConfirmationPassword({ ...confirmationPassword, ...confirmationPasswordValidation });
  };

  const handleConfirmationPasswordChange = (value) => {
    const { isValid, errors } = validateConfirmationPassword(newPassword.value, value);
    setConfirmationPassword({
      value, isValid, errors, dirty: true,
    });
  };

  const trackNewPasswordFormError = () => {
    trackAdobeAnalyticsError(newPassword.errors, 'fe-');
  };

  const trackConfirmationPasswordFormError = () => {
    trackAdobeAnalyticsError(confirmationPassword.errors, 'fe-');
  };

  const showValidationErrors = (password) => {
    if (!password.dirty || password.isValid) {
      return null;
    }
    return password.errors.map((error) => {
      if (!requestSent && (error === PASSWORD_ERRORS.REQUIRED || error === PASSWORD_ERRORS.CONFIRM_PASSWORD_REQUIRED)) {
        return null;
      }
      return <div key={error}>{errorsMessages[error]}</div>;
    });
  };

  const showNewPasswordValidationErrors = () => {
    if (requestSent && !newPassword.dirty) {
      return <div>{errorsMessages[PASSWORD_ERRORS.REQUIRED]}</div>;
    }
    return showValidationErrors(newPassword);
  };

  const showConfirmationPasswordValidationErrors = () => {
    if (requestSent && !confirmationPassword.dirty) {
      return <div>{errorsMessages[PASSWORD_ERRORS.CONFIRM_PASSWORD_REQUIRED]}</div>;
    }
    return showValidationErrors(confirmationPassword);
  };

  return (
    <>
      {isLoading && <SimpleSpinner id={spinnerId} />}
      <Input
        clearButton={false}
        classNameInput="e2e-forgot-password-email-input"
        animatedPlaceholder
        placeholder="New password"
        value={newPassword.value}
        onChange={handleNewPasswordChange}
        onBlur={trackNewPasswordFormError}
        disabled={isLoading}
        required
        validationMessage={showNewPasswordValidationErrors()}
        type="password"
      />

      <Input
        clearButton={false}
        classNameInput="e2e-forgot-password-email-input"
        animatedPlaceholder
        placeholder="Confirm password"
        value={confirmationPassword.value}
        onChange={handleConfirmationPasswordChange}
        onBlur={trackConfirmationPasswordFormError}
        disabled={isLoading}
        required
        type="password"
        validationMessage={showConfirmationPasswordValidationErrors()}
      />
    </>
  );
}

ResetPasswordWithTokenDialogContent.propTypes = {
  trackAdobeAnalyticsError: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  newPassword: PropTypes.object.isRequired,
  setNewPassword: PropTypes.func.isRequired,
  confirmationPassword: PropTypes.object.isRequired,
  setConfirmationPassword: PropTypes.func.isRequired,
  requestSent: PropTypes.bool,
};

export default ResetPasswordWithTokenDialogContent;
